import React, { useState, useEffect } from 'react';
import { apiRequest } from './utils';
import './UserPackages.css';
import translations from './lang';

const UserPackages = () => {
    const language = localStorage.getItem('language') || 'ro';
    const t = translations[language];
    
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [packages, setPackages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentPackage, setCurrentPackage] = useState(null);

    // Fetch user-specific services
    const fetchUserServices = () => {
        apiRequest(`${process.env.REACT_APP_API}/user-services/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
        })
            .then(response => response.json())
            .then(data => setServices(data.data)) // Assuming the response structure is { data: [...] }
            .catch(error => console.error('Error fetching services:', error));
    };

    // Fetch packages for a selected service
    const fetchPackages = (serviceId) => {
        apiRequest(`${process.env.REACT_APP_API}/api/service-packages/${serviceId}/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language  // <== nyelv küldése headerként
            },
        })
        .then(response => response.json())
        .then(data => {
            const updatedPackages = data.data.map(pkg => {
                const duration = parseFloat(pkg.duration);
                const durationHours = Math.floor(duration);
                const durationMinutes = Math.round((duration - durationHours) * 60);

                return {
                    ...pkg,
                    durationHours,
                    durationMinutes,
                };
            });

            setPackages(updatedPackages);
        })
        .catch(error => console.error('Error fetching packages:', error));
    };

    useEffect(() => {
        fetchUserServices();
    }, []);

    useEffect(() => {
        if (selectedService) {
            fetchPackages(selectedService);
        } else {
            setPackages([]);
        }
    }, [selectedService]);

    // Edit existing package
    const handleEdit = (pkg) => {
        const duration = parseFloat(pkg.duration);
        const durationHours = Math.floor(duration);
        const durationMinutes = Math.round((duration - durationHours) * 60);

        setCurrentPackage({
            ...pkg,
            duration_hours: durationHours,
            duration_minutes: durationMinutes,
        });
        setShowModal(true); // Open the modal for editing
    };

    // Delete package
    const handleDelete = (pkgId) => {
        const confirmDelete = window.confirm("Biztosan törölni szeretné ezt a csomagot?");
        if (!confirmDelete) return;
    
        apiRequest(`${process.env.REACT_APP_API}/api/service-packages/${0}/`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify({ package_id: pkgId }), // package_id a body-ban
        })
            .then(async (response) => {
                if (response.status === 200) {
                    setPackages(prevPackages => prevPackages.filter(pkg => pkg.id !== pkgId)); // Frissítsük a listát
                } else {
                    const errorData = await response.json().catch(() => null);
                    const errorMessage = errorData?.error || "Hiba történt a törlés során.";
                    alert(errorMessage);
                }
            })
            .catch(error => {
                console.error('Error deleting package:', error);
                alert("Hálózati hiba történt. Kérjük, próbálja meg később.");
            });
    };

    // Handle save (edit existing package)
    const handleSave = () => {
        const durationInHours = parseFloat(currentPackage.duration_hours || 0) + (parseFloat(currentPackage.duration_minutes || 0) / 60);

        apiRequest(`${process.env.REACT_APP_API}/api/service-packages/${currentPackage.id}/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify({
                package_id: currentPackage.id, // A csomag ID-ját is küldjük a body-ban
                duration: durationInHours.toFixed(2), // Set duration as a number (float)
                ...currentPackage, // A többi adatot is átadjuk
            }),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    fetchPackages(selectedService); // Re-fetch packages after saving
                    setShowModal(false); // Close modal after save
                } else {
                    const errorData = await response.json().catch(() => null);
                    const errorMessage = errorData?.error || "Hiba történt a mentés során.";
                    alert(errorMessage);
                }
            })
            .catch(error => {
                console.error('Error saving package:', error);
                alert("Hálózati hiba történt.");
            });
    };

    // Handle insert (add new package)
    const handleInsert = () => {
        const durationInHours = parseFloat(currentPackage.duration_hours || 0) + (parseFloat(currentPackage.duration_minutes || 0) / 60);

        apiRequest(`${process.env.REACT_APP_API}/api/service-packages/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify({
                service: selectedService,
                duration: durationInHours.toFixed(2),
                ...currentPackage, // Add the rest of the data
            }),
        })
            .then(async (response) => {
                if (response.status === 201) {
                    fetchPackages(selectedService); // Re-fetch packages after insert
                    setShowModal(false); // Close modal after insert
                } else {
                    const errorData = await response.json().catch(() => null);
                    const errorMessage = errorData?.error || "Hiba történt az új csomag hozzáadásában.";
                    alert(errorMessage);
                }
            })
            .catch(error => {
                console.error('Error inserting package:', error);
                alert("Hálózati hiba történt.");
            });
    };

    // Handle input changes in modal
    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentPackage(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="packages">
            <h3>{t.package_title}</h3>
            <div className="packages-selector">
                <label htmlFor="service-select">{t.select_service}:</label>
                <select
                    id="service-select"
                    value={selectedService || ''}
                    onChange={(e) => setSelectedService(e.target.value)}
                >
                    <option value="">{t.choose_service}</option>
                    {services.map(service => (
                        <option key={service.id} value={service.id}>
                            {service.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className="packages-cards">
                {packages.length > 0 ? (
                    packages.map(pkg => (
                        <div key={pkg.id} className="package-card">
                            <div className="package-card-content">
                                <h4>{pkg.name}</h4>
                                <p>{pkg.description}</p>
                                <p><strong>{t.price}:</strong> {pkg.price}</p>
                                <p><strong>{t.duration}:</strong> {pkg.durationHours} {t.hours} {pkg.durationMinutes} {t.minutes}</p>

                            </div>
                            <div className="package-card-buttons">
                                <button onClick={() => handleEdit(pkg)}>{t.edit}</button>
                                <button onClick={() => handleDelete(pkg.id)}>{t.delete}</button>
                            </div>
                        </div>
                    ))
                ) : (
                    selectedService && <p>{t.no_packages}</p>
                )}
                {selectedService && (
                    <div className="package-card empty-card" onClick={() => {
                        setCurrentPackage({
                            name: '',
                            description: '',
                            price: '',
                            duration_hours: 0,
                            duration_minutes: 0,
                            service: selectedService,
                        });
                        setShowModal(true); // Open modal for adding a new package
                    }}>
                        <div className="package-card-content">
                            <h4>{t.add_new_package}</h4>
                            <p>{t.add_package_desc}</p>
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal">
                    <div className="modal-content">
                        <h4>{currentPackage?.id ? t.edit_package : t.new_package}</h4>
                        <label>{t.name}:
                            <input
                                type="text"
                                name="name"
                                value={currentPackage?.name || ''}
                                onChange={handleChange}
                            />
                        </label>
                        <label>{t.description}:
                            <textarea
                                name="description"
                                value={currentPackage?.description || ''}
                                onChange={handleChange}
                            />
                        </label>
                        <label>{t.price}:
                            <input
                                type="number"
                                name="price"
                                value={currentPackage?.price || ''}
                                onChange={handleChange}
                            />
                        </label>
                        <label>{t.duration_hours}:
                            <input
                                type="number"
                                name="duration_hours"
                                value={currentPackage?.duration_hours || ''}
                                onChange={handleChange}
                            />
                        </label>
                        <label>{t.duration_minutes}:
                            <input
                                type="number"
                                name="duration_minutes"
                                value={currentPackage?.duration_minutes || ''}
                                onChange={handleChange}
                            />
                        </label>
                        <button onClick={currentPackage?.id ? handleSave : handleInsert}>
                            {currentPackage?.id ? t.save : t.add}
                        </button>
                        <button onClick={() => setShowModal(false)}>{t.cancel}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserPackages;
